"use client"
import {
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { type PlanAPhaseData, getDataForDate } from "@/data/plan-a/planATypes"
import { PLAN_NODE_IDS, type PlanId } from "@/lib/branchTypes"
import { type LegendMarkerPosition } from "@/app/playbook/globe/LegendText"
import {
  type Globe2Settings,
  type CountryWakeup,
  type StateWakeup,
} from "@/app/playbook/globe/globe2Types"
import { type MarkdownLineCounts } from "@/app/playbook/timeline/timelineUtils"
import { useIsShort } from "@/lib/useIsShort"
import { isDealWindowTrimesterYear } from "../../data/playbookScrollUtils"
import { useAnimatedDate } from "../../hooks/useAnimatedDate"
import { PlaybookGlobe } from "../../PlaybookGlobe"
import { PlaybookExtrapolatedGraph } from "../../PlaybookExtrapolatedGraph"
import { useMetricTrigger } from "../metric-details/useMetricTrigger"
import {
  type ExtrapolatedGraphScaleOverrides,
} from "../../extrapolated-graph/types"
import type { RiskFoomDesignId, RiskFoomPreviewMode, RiskAmbientDesignId, RiskSpaceExplorerMode, RiskTedGuideMode } from "../../extrapolated-graph/RiskFoomPreviewOverlay"
import { useBackgroundPatternSettings } from "../../extrapolated-graph/BackgroundPatternSettingsProvider"
import { useAnimationTuning } from "../../animation/animationTuningContext"
import { resolveJsEasing } from "../../animation/animationTuningDefaults"
import { cameraKeyframesData } from "../../globe/cameraSettings"

export type PlaybookHUDProps = {
  currentDate: Date
  planAData: PlanAPhaseData[]
  currentBranchNodeTitle?: string
  selectedPlan: PlanId | null
  activePlan: PlanId
  hoveredPlan: PlanId | null
  // True while the choose-your-path block intersects the viewport — drives
  // the graph's all-plans preview fan (see PlaybookExtrapolatedGraph's
  // choicePointOnScreen prop).
  isChoicePointOnScreen?: boolean
  pageTextColor: string
  pageBackgroundColor: string
  globeSettings?: Globe2Settings
  countryWakeup?: CountryWakeup[]
  stateWakeup?: StateWakeup[]
  legendMarkerPositions: LegendMarkerPosition[]
  markdownLineCounts?: MarkdownLineCounts
  selectedPath: string[]
  hoveredBranchTargetId?: string | null
  isEpilogueExpanded?: boolean
  // True while the Epilogue button (end-cap nav grid) is on screen or above
  // it — i.e. from the Epilogue click until the reader scrolls back up past
  // the grid. Drives Earth-centered mode. See useGalaxy.
  isEpilogueScrolledTo?: boolean
  showGalaxy?: boolean
  // True while branch-hover autoplay drives the date; the globe holds its
  // pre-epilogue framing (skips the 2041+ zoom-away keyframes).
  isHoverPreviewActive?: boolean
  isAgentPopulationHidden: boolean
  isPolicyGraphHidden: boolean
  onNodeSelect: (nodeId: string, scrollTargetId?: string) => void
}

type EpilogueGlobeTranslate = {
  x: number
  y: number
}

export type HudEpilogueState = {
  isEpilogueHudMode: boolean
  // Graph render height; tweens to EPILOGUE_GRAPH_HEIGHT_PX in epilogue mode
  // on the same clock as the move transforms (see the rAF tween below).
  graphHeight: number
  hudContainerRef: MutableRefObject<HTMLDivElement | null>
  globeContainerRef: MutableRefObject<HTMLDivElement | null>
  spaceParcelRef: MutableRefObject<HTMLDivElement | null>
  // The graph's in-flow container (HudPersistentGlobeAndGraph attaches it) —
  // its layout offset anchors the epilogue slide-down transform.
  graphFlowContainerRef: MutableRefObject<HTMLDivElement | null>
  graphTranslateY: number | null
  globeTranslate: EpilogueGlobeTranslate | null
  epilogueHiddenStyle: CSSProperties
  graphContainerStyle: CSSProperties
  globeContainerStyle: CSSProperties
}

// The HUD context is SPLIT by update frequency so the animated date doesn't
// re-render every block every frame:
// - HudDatesContextValue (useHudDates) carries the per-frame values — the
//   spring-animated date and the wakeup arrays derived from it. Only the
//   graph, globe, and other continuously-animating consumers subscribe.
// - HudContextValue (useHudContext) carries everything else and is stable
//   between discrete changes (phase flips, plan/hover changes, epilogue
//   moves), so year/phase-keyed panels stop re-rendering during animation.
export type HudDatesContextValue = {
  animatedDate: Date
  targetDate: Date
  // Alias of animatedDate (the name most date consumers historically used).
  currentDate: Date
  // animatedDate clamped for the space parcel (see
  // EPILOGUE_SPACE_PARCEL_START_DATE) — always clamped so the hidden parcel
  // renders its 2040 rows pre-entry and its height is measurable.
  parcelDate: Date
  countryWakeup?: CountryWakeup[]
  stateWakeup?: StateWakeup[]
}

export type HudContextValue =
  Omit<PlaybookHUDProps, 'currentDate' | 'countryWakeup' | 'stateWakeup'> & {
  year: number
  // Decimal-year column for the distribution-of-power block: Jan-1 values
  // (the year itself) for every story year, except 2029-2030 — the deal
  // window, where the story carries month-level dates — which step through
  // the power-distribution tab's trimester columns (Jan/May/Sep → .0/.33/.67).
  powerColumnDate: number
  isShortScreen: boolean
  currentPhase: PlanAPhaseData | null
  isFirstChoicePlanHovered: boolean
  hasScrolledPastThreshold: boolean
  isHudHovered: boolean
  setIsHudHovered: (hovered: boolean) => void
  galaxyHideStyle: CSSProperties
  galaxyHideStyleWithScrollFade: CSSProperties
  epilogue: HudEpilogueState
}

export const EPILOGUE_GRAPH_SPACE_PARCEL_GAP_PX = 4
export const EPILOGUE_SPACE_PARCEL_START_DATE = new Date("2040-01-01")

export const PRIMARY_GRAPH_HEIGHT_PX = 385

// Epilogue graph height: half the standard graph. It tweens there on the
// same clock as the slide-down, so the graph's bottom edge glides straight
// to its slot EPILOGUE_GRAPH_SPACE_PARCEL_GAP_PX above the space parcel
// without ever crossing into it.
export const EPILOGUE_GRAPH_HEIGHT_PX = Math.round(PRIMARY_GRAPH_HEIGHT_PX / 2)

// Layout (untransformed) offset of `element` within `ancestor` via the
// offsetParent chain — getBoundingClientRect would bake in the epilogue
// move transforms, breaking re-measurement mid-transition.
const sumLayoutOffsetsWithin = (element: HTMLElement, ancestor: HTMLElement) => {
  let x = 0
  let y = 0
  let current: HTMLElement | null = element
  while (current && current !== ancestor) {
    x += current.offsetLeft
    y += current.offsetTop
    current = current.offsetParent as HTMLElement | null
  }
  return { x, y }
}

// The epilogue transition never swaps positioning schemes or unmounts the
// moving pieces: the graph stays in flow (transform-driven), the globe keeps
// its absolute anchor (transform-driven), and the parcel stays mounted
// (opacity/transform-driven). Everything is a CSS transition or a retargeted
// tween on a stable element, so scrolling back up — even mid-transition —
// plays the whole choreography smoothly in reverse.
const useHudEpilogueMode = (
  isEpilogueExpanded: boolean | undefined,
  isEpilogueScrolledTo: boolean | undefined,
): HudEpilogueState => {
  // Live-tunable epilogue timing (see animationTuningContext / the studio's
  // "Epilogue animation" section): defaults to the saved JSON everywhere;
  // overridden only inside /hud-studio. The rAF height tween reads the move
  // duration + JS-easing through refs so a mid-flight tween picks up edits.
  const tuning = useAnimationTuning()
  const { epilogueGlobeMoveMs, epilogueGraphMoveMs, epilogueHudFadeMs, epilogueMoveEasing } = tuning
  const graphMoveMsRef = useRef(epilogueGraphMoveMs)
  graphMoveMsRef.current = epilogueGraphMoveMs
  const moveEaseRef = useRef<(t: number) => number>(resolveJsEasing(epilogueMoveEasing))
  moveEaseRef.current = useMemo(() => resolveJsEasing(epilogueMoveEasing), [epilogueMoveEasing])

  const hudContainerRef = useRef<HTMLDivElement | null>(null)
  const globeContainerRef = useRef<HTMLDivElement | null>(null)
  const spaceParcelRef = useRef<HTMLDivElement | null>(null)
  const graphFlowContainerRef = useRef<HTMLDivElement | null>(null)
  const [spaceParcelHeight, setSpaceParcelHeight] = useState(0)
  const [graphTranslateY, setGraphTranslateY] = useState<number | null>(null)
  const [globeTranslate, setGlobeTranslate] = useState<EpilogueGlobeTranslate | null>(null)

  const isEpilogueHudMode = !!isEpilogueExpanded && !!isEpilogueScrolledTo

  // Graph height, tweened between the standard and epilogue sizes on the
  // graph-move clock. Retargets from the current value on reversal.
  const [graphHeight, setGraphHeight] = useState(PRIMARY_GRAPH_HEIGHT_PX)
  const graphHeightRef = useRef(PRIMARY_GRAPH_HEIGHT_PX)
  useEffect(() => {
    const target = isEpilogueHudMode ? EPILOGUE_GRAPH_HEIGHT_PX : PRIMARY_GRAPH_HEIGHT_PX
    const from = graphHeightRef.current
    if (from === target) return
    const startTime = performance.now()
    let frameId: number
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startTime) / graphMoveMsRef.current)
      const value = from + (target - from) * moveEaseRef.current(progress)
      graphHeightRef.current = value
      setGraphHeight(value)
      if (progress < 1) frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [isEpilogueHudMode])

  // The graph's slide is transitioned ONLY around an epilogue-mode change
  // (the enter/exit move). Once the move settles, the window closes and
  // translate retargets apply with NO transition — the parcel's row
  // enter/exit animations already tween its height smoothly, and the
  // ResizeObserver streams that height per frame, so the graph rides
  // EPILOGUE_GRAPH_SPACE_PARCEL_GAP_PX above the parcel's top edge in
  // exact lockstep instead of chasing it on
  // a long transition. Any retarget while the window is open (e.g. a row
  // appearing mid-entry) re-arms the timer so an in-flight transition is
  // never cut short.
  const [isGraphMoveWindowOpen, setIsGraphMoveWindowOpen] = useState(false)
  useEffect(() => {
    setIsGraphMoveWindowOpen(true)
  }, [isEpilogueHudMode])
  useEffect(() => {
    if (!isGraphMoveWindowOpen) return
    const timeoutId = window.setTimeout(
      () => setIsGraphMoveWindowOpen(false),
      graphMoveMsRef.current
    )
    return () => window.clearTimeout(timeoutId)
  }, [isGraphMoveWindowOpen, graphTranslateY])

  // Parcel height observer — always on (the parcel stays mounted, hidden,
  // outside the epilogue) so the graph's landing slot is already known the
  // moment the transition starts.
  useEffect(() => {
    const spaceParcelElement = spaceParcelRef.current
    if (!spaceParcelElement) return
    const updateSpaceParcelHeight = () => setSpaceParcelHeight(spaceParcelElement.offsetHeight)
    updateSpaceParcelHeight()
    const resizeObserver = new ResizeObserver(updateSpaceParcelHeight)
    resizeObserver.observe(spaceParcelElement)
    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    if (!isEpilogueHudMode) {
      setGraphTranslateY(null)
      return
    }
    const updateGraphTranslate = () => {
      const hudContainerElement = hudContainerRef.current
      const graphFlowElement = graphFlowContainerRef.current
      if (!hudContainerElement || !graphFlowElement) return
      const { y: graphFlowTop } = sumLayoutOffsetsWithin(graphFlowElement, hudContainerElement)
      setGraphTranslateY(
        hudContainerElement.offsetHeight
        - spaceParcelHeight
        - EPILOGUE_GRAPH_SPACE_PARCEL_GAP_PX
        - EPILOGUE_GRAPH_HEIGHT_PX
        - graphFlowTop
      )
    }
    updateGraphTranslate()
    window.addEventListener("resize", updateGraphTranslate)
    return () => window.removeEventListener("resize", updateGraphTranslate)
  }, [isEpilogueHudMode, spaceParcelHeight])

  useEffect(() => {
    if (!isEpilogueHudMode) {
      setGlobeTranslate(null)
      return
    }
    const updateGlobeTranslate = () => {
      const hudContainerElement = hudContainerRef.current
      const globeContainerElement = globeContainerRef.current
      if (!hudContainerElement || !globeContainerElement) return
      // Untransformed center (offset chain), so re-entering mid-exit
      // measures the same target as a clean entry.
      const globeOffsets = sumLayoutOffsetsWithin(globeContainerElement, hudContainerElement)
      const globeCenterX = globeOffsets.x + globeContainerElement.offsetWidth / 2
      const globeCenterY = globeOffsets.y + globeContainerElement.offsetHeight / 2
      const hudRect = hudContainerElement.getBoundingClientRect()
      setGlobeTranslate({
        x: hudRect.width / 2 - globeCenterX,
        y: window.innerHeight / 2 - hudRect.top - globeCenterY,
      })
    }
    updateGlobeTranslate()
    window.addEventListener("resize", updateGlobeTranslate)
    return () => window.removeEventListener("resize", updateGlobeTranslate)
  }, [isEpilogueHudMode])

  // Memoized so the epilogue object (and thus the whole static HUD context)
  // keeps a stable identity between actual epilogue changes — the provider
  // itself re-renders every animation frame.
  return useMemo<HudEpilogueState>(() => {
    // visibility (delayed past the fade) drops hidden blocks out of the tab
    // order too — opacity/pointer-events alone leave them keyboard-focusable.
    // Only ever SET hidden/none, never force visible/auto: an explicit value
    // on a child overrides an ancestor's hidden or pointer-events-none and
    // resurrects content inside the HUD's crossfaded-away layers as an
    // invisible click target.
    const epilogueHiddenStyle: CSSProperties = {
      opacity: isEpilogueHudMode ? 0 : 1,
      visibility: isEpilogueHudMode ? 'hidden' : undefined,
      transition: `opacity ${epilogueHudFadeMs}ms ease-in-out, visibility 0s ${isEpilogueHudMode ? `${epilogueHudFadeMs}ms` : '0s'}`,
      pointerEvents: isEpilogueHudMode ? 'none' : undefined,
    }
    // Transforms are explicit translate(0)s (not undefined) outside the
    // epilogue, so both entry and exit animate — including retargeting
    // mid-flight. The transition only exists during the enter/exit move
    // window; afterwards the graph tracks the parcel height untransitioned
    // (see isGraphMoveWindowOpen above).
    const graphContainerStyle: CSSProperties = {
      maxWidth: 455,
      transform: graphTranslateY !== null
        ? `translateY(${graphTranslateY}px)`
        : 'translateY(0px)',
      transition: isGraphMoveWindowOpen
        ? `transform ${epilogueGraphMoveMs}ms ${epilogueMoveEasing}`
        : 'none',
    }
    const globeContainerStyle: CSSProperties = {
      transform: globeTranslate
        ? `translate(${globeTranslate.x}px, ${globeTranslate.y}px)`
        : 'translate(0px, 0px)',
      transition: `transform ${epilogueGlobeMoveMs}ms ${epilogueMoveEasing}`,
    }
    return {
      isEpilogueHudMode,
      graphHeight,
      hudContainerRef,
      globeContainerRef,
      spaceParcelRef,
      graphFlowContainerRef,
      graphTranslateY,
      globeTranslate,
      epilogueHiddenStyle,
      graphContainerStyle,
      globeContainerStyle,
    }
  }, [
    isEpilogueHudMode,
    graphHeight,
    graphTranslateY,
    globeTranslate,
    isGraphMoveWindowOpen,
    epilogueGlobeMoveMs,
    epilogueGraphMoveMs,
    epilogueHudFadeMs,
    epilogueMoveEasing,
  ])
}

const HudContextRef = createContext<HudContextValue | null>(null)
const HudDatesContextRef = createContext<HudDatesContextValue | null>(null)

type HudContextProviderProps = PlaybookHUDProps & {
  children: ReactNode
}

export const HudContextProvider = ({ children, ...props }: HudContextProviderProps) => {
  const animatedDate = useAnimatedDate(props.currentDate)
  const targetDate = props.currentDate
  const isShortScreen = useIsShort()
  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false)
  const [isHudHovered, setIsHudHovered] = useState(false)

  useEffect(() => {
    const updateScrolled = () => setHasScrolledPastThreshold(window.scrollY > 30)
    updateScrolled()
    window.addEventListener("scroll", updateScrolled, { passive: true })
    return () => window.removeEventListener("scroll", updateScrolled)
  }, [])

  // Discrete, stepped panels (the year-keyed power treemap, phase-keyed
  // metrics/policies) key on the TARGET date, not the tweened one: they flip
  // at the very start of the shared HUD_SYNC_TRANSITION_MS window so their
  // own ≤500ms transitions complete inside it. Keyed on animatedDate they'd
  // flip whenever the tween happened to cross their threshold — partway
  // through the window — and visibly lag the graph.
  const year = targetDate.getFullYear()
  // Stepped like `year` (target date, not the tween): annual Jan-1 columns,
  // except through the 2029-2030 deal window where the block advances at the
  // power-distribution tab's trimester columns (Jan/May/Sep → .0/.33/.67).
  // UTC like the year pill and snapTimeToThirdOfYear: deal-window dates land
  // on UTC trimester starts, which local accessors would read as the previous
  // month (Apr 30) and lag the pill by a whole trimester.
  const powerUtcYear = targetDate.getUTCFullYear()
  const powerColumnDate = isDealWindowTrimesterYear(powerUtcYear)
    ? powerUtcYear + Math.floor(targetDate.getUTCMonth() / 4) / 3
    : powerUtcYear

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Debug tap for date-pipeline diagnosis (see the perf/smoothness hunt
      // tooling): the raw scroll-driven target vs the spring-tweened date.
      ;(window as unknown as Record<string, unknown>).__hudDates = {
        target: targetDate.getTime(),
        animated: animatedDate.getTime(),
      }
    }
  }, [targetDate, animatedDate])

  const currentPhase = useMemo(
    () => getDataForDate(props.planAData, targetDate),
    [props.planAData, targetDate]
  )
  const isFirstChoicePlanHovered = props.hoveredBranchTargetId
    ? Object.values(PLAN_NODE_IDS).includes(props.hoveredBranchTargetId)
    : false

  // visibility (delayed past the fade) drops hidden blocks out of the tab
  // order too — opacity/pointer-events alone leave them keyboard-focusable.
  // Only ever SET hidden/none, never force visible/auto (see epilogueHiddenStyle).
  const galaxyHideStyle = useMemo<CSSProperties>(() => ({
    opacity: props.showGalaxy ? 0 : 1,
    height: props.showGalaxy ? 0 : 'auto',
    visibility: props.showGalaxy ? 'hidden' : undefined,
    transition: `opacity 600ms ease-in-out, visibility 0s ${props.showGalaxy ? '600ms' : '0s'}`,
    pointerEvents: props.showGalaxy ? 'none' : undefined,
  }), [props.showGalaxy])

  const galaxyHideStyleWithScrollFade = useMemo<CSSProperties>(
    () => {
      const hidden = props.showGalaxy || !hasScrolledPastThreshold
      return {
        ...galaxyHideStyle,
        opacity: hidden ? 0 : 1,
        visibility: hidden ? 'hidden' : undefined,
        transition: `opacity 600ms ease-in-out, visibility 0s ${hidden ? '600ms' : '0s'}`,
      }
    },
    [galaxyHideStyle, props.showGalaxy, hasScrolledPastThreshold]
  )

  const epilogue = useHudEpilogueMode(props.isEpilogueExpanded, props.isEpilogueScrolledTo)

  // Per-frame values live in their own context (see HudDatesContextValue):
  // countryWakeup/stateWakeup ride along because the caller derives them from
  // the animated date, so their identities churn every frame too.
  const {
    currentDate: _currentDateProp,
    countryWakeup,
    stateWakeup,
    ...staticProps
  } = props
  const datesValue = useMemo<HudDatesContextValue>(() => ({
    animatedDate,
    targetDate,
    currentDate: animatedDate,
    parcelDate: animatedDate.getTime() < EPILOGUE_SPACE_PARCEL_START_DATE.getTime()
      ? EPILOGUE_SPACE_PARCEL_START_DATE
      : animatedDate,
    countryWakeup,
    stateWakeup,
  }), [animatedDate, targetDate, countryWakeup, stateWakeup])

  // The static context value must stay reference-stable across animation
  // frames (the provider re-renders every frame), so its memo deps enumerate
  // the individual prop fields rather than the per-render `props` object.
  const value = useMemo<HudContextValue>(() => ({
    ...staticProps,
    year,
    powerColumnDate,
    isShortScreen,
    currentPhase,
    isFirstChoicePlanHovered,
    hasScrolledPastThreshold,
    isHudHovered,
    setIsHudHovered,
    galaxyHideStyle,
    galaxyHideStyleWithScrollFade,
    epilogue,
  }), [
    // eslint-disable-next-line react-hooks/exhaustive-deps -- staticProps is
    // per-render; its fields are enumerated below instead.
    staticProps.planAData,
    staticProps.currentBranchNodeTitle,
    staticProps.selectedPlan,
    staticProps.activePlan,
    staticProps.hoveredPlan,
    staticProps.isChoicePointOnScreen,
    staticProps.pageTextColor,
    staticProps.pageBackgroundColor,
    staticProps.globeSettings,
    staticProps.legendMarkerPositions,
    staticProps.markdownLineCounts,
    staticProps.selectedPath,
    staticProps.hoveredBranchTargetId,
    staticProps.isEpilogueExpanded,
    staticProps.isEpilogueScrolledTo,
    staticProps.showGalaxy,
    staticProps.isHoverPreviewActive,
    staticProps.isAgentPopulationHidden,
    staticProps.isPolicyGraphHidden,
    staticProps.onNodeSelect,
    year,
    powerColumnDate,
    isShortScreen,
    currentPhase,
    isFirstChoicePlanHovered,
    hasScrolledPastThreshold,
    isHudHovered,
    galaxyHideStyle,
    galaxyHideStyleWithScrollFade,
    epilogue,
  ])

  return (
    <HudContextRef.Provider value={value}>
      <HudDatesContextRef.Provider value={datesValue}>
        {children}
      </HudDatesContextRef.Provider>
    </HudContextRef.Provider>
  )
}

// Fallback for useHudContext() outside <HudContextProvider>; earliest
// keyframe date so year-driven panels show timeline start, not today.
const EARLIEST_HUD_FALLBACK_DATE = new Date(
  Object.keys(cameraKeyframesData).sort()[0]
)
const FALLBACK_EPILOGUE: HudEpilogueState = {
  isEpilogueHudMode: false,
  graphHeight: PRIMARY_GRAPH_HEIGHT_PX,
  hudContainerRef: { current: null },
  globeContainerRef: { current: null },
  spaceParcelRef: { current: null },
  graphFlowContainerRef: { current: null },
  graphTranslateY: null,
  globeTranslate: null,
  epilogueHiddenStyle: {},
  graphContainerStyle: {},
  globeContainerStyle: {},
}
const FALLBACK_HUD_DATES: HudDatesContextValue = {
  animatedDate: EARLIEST_HUD_FALLBACK_DATE,
  targetDate: EARLIEST_HUD_FALLBACK_DATE,
  currentDate: EARLIEST_HUD_FALLBACK_DATE,
  parcelDate: EARLIEST_HUD_FALLBACK_DATE,
  countryWakeup: undefined,
  stateWakeup: undefined,
}
const FALLBACK_HUD_CONTEXT: HudContextValue = {
  planAData: [],
  currentBranchNodeTitle: undefined,
  selectedPlan: null,
  activePlan: 'A',
  hoveredPlan: null,
  isChoicePointOnScreen: false,
  pageTextColor: '#000',
  pageBackgroundColor: '#fffff8',
  globeSettings: undefined,
  legendMarkerPositions: [],
  markdownLineCounts: undefined,
  selectedPath: [],
  hoveredBranchTargetId: null,
  isEpilogueExpanded: false,
  isEpilogueScrolledTo: false,
  showGalaxy: false,
  isHoverPreviewActive: false,
  isAgentPopulationHidden: false,
  isPolicyGraphHidden: false,
  onNodeSelect: () => {},
  year: EARLIEST_HUD_FALLBACK_DATE.getFullYear(),
  powerColumnDate: EARLIEST_HUD_FALLBACK_DATE.getFullYear(),
  isShortScreen: false,
  currentPhase: null,
  isFirstChoicePlanHovered: false,
  hasScrolledPastThreshold: false,
  isHudHovered: false,
  setIsHudHovered: () => {},
  galaxyHideStyle: {},
  galaxyHideStyleWithScrollFade: {},
  epilogue: FALLBACK_EPILOGUE,
}

export const useHudContext = (): HudContextValue => {
  return useContext(HudContextRef) ?? FALLBACK_HUD_CONTEXT
}

// Per-frame animated dates (+ the wakeup arrays derived from them). Only
// subscribe from components that genuinely re-render every animation frame —
// everything else should use useHudContext(), which stays reference-stable
// while the date springs run.
export const useHudDates = (): HudDatesContextValue => {
  return useContext(HudDatesContextRef) ?? FALLBACK_HUD_DATES
}

// How far below its `fixed top-6` anchor the HUD — and the details panel that
// shares its slot — rests before sliding up over the first 100px of scroll.
export const HUD_SLIDE_UP_FROM_PX = 140

type HudSlideUpStylesProps = {
  slideUpId: string
  fromTranslateYPx?: number
}

export const HudSlideUpStyles = ({
  slideUpId,
  fromTranslateYPx = 100,
}: HudSlideUpStylesProps) => {
  const slideKeyframe = `playbook-hud-${slideUpId}-slide-up`
  const containerClass = `playbook-hud-${slideUpId}-scroll-slide`
  // The HUD rests `fromTranslateYPx` below its `fixed top-6` anchor and slides
  // up to 0 over the first 100px of scroll. Chrome drives this with a scroll
  // timeline. Firefox lacks scroll timelines (pref off by default), so:
  //  - the base rule rests at translateY(fromTranslateYPx), no animation
  //  - @supports adds the real scroll animation for Chrome
  //  - useScrollSlideFallback() drives --hud-slide-y from JS for Firefox
  return (
    <style>{`
      @keyframes ${slideKeyframe} {
        from { transform: translateY(${fromTranslateYPx}px); }
        to { transform: translateY(0); }
      }
      .${containerClass} {
        transform: translateY(var(--hud-slide-y, ${fromTranslateYPx}px));
      }
      @supports (animation-timeline: scroll()) {
        .${containerClass} {
          transform: none;
          animation: ${slideKeyframe} linear both;
          animation-timeline: scroll(root);
          animation-range: 0 100px;
        }
      }
    `}</style>
  )
}

// Drives the slide via JS where scroll timelines are unsupported (Firefox);
// no-op where they are (Chrome's CSS animation wins). Attach the ref to the
// hudSlideUpClass element.
const SLIDE_UP_SCROLL_RANGE_PX = 100
export const useScrollSlideFallback = (fromTranslateYPx: number) => {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (CSS?.supports?.("animation-timeline: scroll()")) return
    const element = ref.current
    if (!element) return
    const updateSlide = () => {
      const progress = Math.min(window.scrollY, SLIDE_UP_SCROLL_RANGE_PX) / SLIDE_UP_SCROLL_RANGE_PX
      element.style.setProperty("--hud-slide-y", `${fromTranslateYPx * (1 - progress)}px`)
    }
    updateSlide()
    window.addEventListener("scroll", updateSlide, { passive: true })
    return () => window.removeEventListener("scroll", updateSlide)
  }, [fromTranslateYPx])
  return ref
}

export const hudSlideUpClass = (slideUpId: string) => `playbook-hud-${slideUpId}-scroll-slide`

// Building blocks for panels in app/playbook/hud/blocks/, so globe/graph
// wiring changes stay localized instead of spreading across panels.
type HudGlobeBlockProps = {
  className?: string
  style?: CSSProperties
  containerRef?: MutableRefObject<HTMLDivElement | null>
  innerClassName?: string
  // Style for the inner wrapper — carries the epilogue move transform so its
  // `transition` never collides with the outer block's galaxy-hide fade.
  innerStyle?: CSSProperties
  size?: number
  lockSizeToInitialHeight?: boolean
  minDevicePixelRatio?: number
  paused?: boolean
}

export const HudGlobeBlock = ({
  className,
  style,
  containerRef,
  innerClassName,
  innerStyle,
  size,
  lockSizeToInitialHeight,
  minDevicePixelRatio,
  paused,
}: HudGlobeBlockProps) => {
  const ctx = useHudContext()
  const hudDates = useHudDates()
  // Pause the globe while its block is scrolled out of the viewport. The
  // globe's frameloop otherwise runs "always": on mobile the hero block
  // leaves the screen a few paragraphs in and its globe kept doing 6-8 full
  // render passes per frame, invisibly, for the whole read — the largest
  // sustained GPU load on the page and a prime suspect in the mobile Safari
  // crash. The generous rootMargin resumes it just before it scrolls back
  // in, so the reader never sees a stale frame.
  const offscreenObserverRef = useRef<HTMLDivElement | null>(null)
  const [isOffscreen, setIsOffscreen] = useState(false)
  useEffect(() => {
    const element = offscreenObserverRef.current
    if (!element || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) setIsOffscreen(!entry.isIntersecting)
      },
      { rootMargin: "300px 0px" }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  const effectivePaused = (paused ?? false) || isOffscreen
  // Freeze date while paused so memoized globe skips per-frame re-renders.
  const lastUnpausedDateRef = useRef(hudDates.animatedDate)
  if (!effectivePaused) lastUnpausedDateRef.current = hudDates.animatedDate
  const globeDate = effectivePaused
    ? lastUnpausedDateRef.current
    : hudDates.animatedDate
  return (
    <div
      ref={element => {
        offscreenObserverRef.current = element
        if (containerRef) containerRef.current = element
      }}
      className={className}
      style={style}
    >
      <div className={innerClassName ?? "pointer-events-none"} style={innerStyle}>
        <PlaybookGlobe
          currentDate={globeDate}
          activePlan={ctx.activePlan}
          globeSettings={ctx.globeSettings}
          countryWakeup={hudDates.countryWakeup}
          stateWakeup={hudDates.stateWakeup}
          legendMarkerPositions={ctx.legendMarkerPositions}
          size={size}
          lockSizeToInitialHeight={lockSizeToInitialHeight}
          minDevicePixelRatio={minDevicePixelRatio}
          paused={effectivePaused}
          isHoverPreviewActive={ctx.isHoverPreviewActive}
        />
      </div>
    </div>
  )
}

// In the epilogue the capability line's solid (red) past segment and dashed
// future segment both turn black for the epilogue backdrop.
// The lines are canvas strokes, so they can't CSS-transition — the recolor is
// blended in JS on the same clock + easing as the graph's slide-down.
const EPILOGUE_CAPABILITY_LINE_COLOR = '#000000'

// Blend two #rgb/#rrggbb hex colors; non-hex inputs (e.g. var() strings)
// fall back to a midpoint snap.
const mixHexColors = (from: string, to: string, t: number): string => {
  if (t <= 0) return from
  if (t >= 1) return to
  const parse = (hex: string): [number, number, number] | null => {
    const raw = hex.replace('#', '')
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ]
  }
  const fromRgb = parse(from)
  const toRgb = parse(to)
  if (!fromRgb || !toRgb) return t < 0.5 ? from : to
  const channel = (i: number) => Math.round(fromRgb[i] + (toRgb[i] - fromRgb[i]) * t)
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`
}

// 0 outside the epilogue, 1 inside, tweened on the graph-move clock —
// retargeting from the current value on reversal (same pattern as the
// epilogue graph-height tween in useHudEpilogueMode).
const useEpilogueLineColorBlend = (isEpilogueHudMode: boolean): number => {
  const { epilogueGraphMoveMs, epilogueMoveEasing } = useAnimationTuning()
  const moveMsRef = useRef(epilogueGraphMoveMs)
  moveMsRef.current = epilogueGraphMoveMs
  const moveEaseRef = useRef<(t: number) => number>(resolveJsEasing(epilogueMoveEasing))
  moveEaseRef.current = useMemo(() => resolveJsEasing(epilogueMoveEasing), [epilogueMoveEasing])
  const [blend, setBlend] = useState(isEpilogueHudMode ? 1 : 0)
  const blendRef = useRef(blend)
  useEffect(() => {
    const target = isEpilogueHudMode ? 1 : 0
    const from = blendRef.current
    if (from === target) return
    const startTime = performance.now()
    let frameId: number
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startTime) / moveMsRef.current)
      const value = from + (target - from) * moveEaseRef.current(progress)
      blendRef.current = value
      setBlend(value)
      if (progress < 1) frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [isEpilogueHudMode])
  return blend
}

type HudExtrapolatedGraphBlockProps = {
  width?: number
  height?: number
  showLossOfControlLines?: boolean
  showAICapabilitiesLabel?: boolean
  redAboveLossOfControl?: boolean
  blackBelowTooSlowRisk?: boolean
  // See ExtrapolatedGraphProps for these (HUD 87 graph restyle knobs).
  currentYearPill?: boolean
  onCurrentYearPillClick?: () => void
  currentYearPillMenuOpen?: boolean
  capabilityPastLineColor?: string
  capabilityPastLineStrokeWidth?: number
  // Clips the capability line to the red dither field's top edge (tab 49) —
  // see clipLinesToDitherTop in extrapolated-graph/types.ts.
  clipLinesToDitherTop?: boolean
  capabilityLineFloorValue?: number
  showCurrentPointMarker?: boolean
  currentPointMarkerRadius?: number
  showCurrentPointCapabilityLabel?: boolean
  // Floor for the graph's current/target date. The top-of-article
  // data-chart-date marker rewinds the HUD to 2025-01; clamping keeps the
  // graph (and its red line) from collapsing to the timeline start there.
  minimumGraphDate?: Date
  showAllYears?: boolean
  showYearDots?: boolean
  numCapabilityThresholdLabels?: number
  showCapabilityThresholdLabels?: boolean
  showRiskBackgroundLabels?: boolean
  safetyTaxShiftMonths?: number
  alignmentDoublings?: number
  lossOfControlHoverRevealOverride?: boolean
  useAnimatedDateForViewport?: boolean
  animateViewportScale?: boolean
  animateHeight?: boolean
  scaleOverrides?: ExtrapolatedGraphScaleOverrides
  paused?: boolean
  // See PlaybookExtrapolatedGraph's hideGraphExplainerTooltip prop.
  hideGraphExplainerTooltip?: boolean
  // See ExtrapolatedGraphRenderProps.pointHoverMaxDistanceToLinePx.
  pointHoverMaxDistanceToLinePx?: number
  // See ExtrapolatedGraphRenderProps.takeoverRiskAreaHover.
  takeoverRiskAreaHover?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomPreview.
  riskFoomPreview?: RiskFoomPreviewMode
  // See ExtrapolatedGraphRenderProps.riskFoomDesign.
  riskFoomDesign?: RiskFoomDesignId
  // See ExtrapolatedGraphRenderProps.riskFoomTedCaption.
  riskFoomTedCaption?: boolean
  // See ExtrapolatedGraphRenderProps.riskFoomOnPointHover.
  riskFoomOnPointHover?: boolean
  // See ExtrapolatedGraphProps.riskSpaceExplorer (the 2D risk-space explorer).
  riskSpaceExplorer?: RiskSpaceExplorerMode
  // See ExtrapolatedGraphProps.riskTedGuide (the tab-26 TED guide).
  riskTedGuide?: RiskTedGuideMode
  // See ExtrapolatedGraphProps.riskAmbientDisplay (the always-on readout).
  riskAmbientDisplay?: RiskAmbientDesignId
  // See ExtrapolatedGraphProps.riskHoverDimDisabled (tab 53: no field dim).
  riskHoverDimDisabled?: boolean
  // See PlaybookExtrapolatedGraph.preChoiceYAxisMax (tab 33 close-up zoom).
  preChoiceYAxisMax?: number
  // See PlaybookExtrapolatedGraph.useLogScale (tab 33 pairs it with the
  // zoomed-out close-up so the line keeps a readable curve).
  graphLogScale?: boolean
  // See ExtrapolatedGraphRenderProps.highlightLineWhilePointHovered.
  highlightLineWhilePointHovered?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimTween (tabs 66–70).
  riskZoneDimTween?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimInverted (tab 70).
  riskZoneDimInverted?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneDimDotShrink (picks tab 2).
  riskZoneDimDotShrink?: boolean
  // See ExtrapolatedGraphRenderProps.riskZoneContinuousGuide (tab 68).
  riskZoneContinuousGuide?: boolean
  // See ExtrapolatedGraphRenderProps.pointGuideTedDash.
  pointGuideTedDash?: boolean
  // False for decorative instances (e.g. the mobile HUD's pointer-inert
  // previews) so they don't render as focusable metric-details triggers.
  detailsTrigger?: boolean
}

export const HudExtrapolatedGraphBlock = ({
  width,
  height,
  showLossOfControlLines,
  showAICapabilitiesLabel,
  redAboveLossOfControl,
  blackBelowTooSlowRisk,
  currentYearPill,
  onCurrentYearPillClick,
  currentYearPillMenuOpen,
  capabilityPastLineColor,
  capabilityPastLineStrokeWidth,
  clipLinesToDitherTop,
  capabilityLineFloorValue,
  showCurrentPointMarker,
  currentPointMarkerRadius,
  showCurrentPointCapabilityLabel,
  minimumGraphDate,
  showAllYears,
  showYearDots,
  numCapabilityThresholdLabels,
  showCapabilityThresholdLabels,
  showRiskBackgroundLabels,
  safetyTaxShiftMonths = 0,
  alignmentDoublings = 0,
  lossOfControlHoverRevealOverride,
  useAnimatedDateForViewport,
  animateViewportScale,
  animateHeight,
  scaleOverrides,
  paused,
  hideGraphExplainerTooltip,
  pointHoverMaxDistanceToLinePx,
  takeoverRiskAreaHover,
  riskFoomPreview,
  riskFoomDesign,
  riskFoomTedCaption,
  riskFoomOnPointHover,
  riskSpaceExplorer,
  riskTedGuide,
  riskAmbientDisplay,
  riskHoverDimDisabled,
  preChoiceYAxisMax,
  graphLogScale,
  highlightLineWhilePointHovered,
  riskZoneDimTween,
  riskZoneDimInverted,
  riskZoneDimDotShrink,
  riskZoneContinuousGuide,
  pointGuideTedDash,
  detailsTrigger = true,
}: HudExtrapolatedGraphBlockProps) => {
  const ctx = useHudContext()
  const hudDates = useHudDates()
  // Freeze dates while paused so memoized graph skips per-frame re-renders.
  const lastUnpausedAnimatedDateRef = useRef(hudDates.animatedDate)
  const lastUnpausedTargetDateRef = useRef(hudDates.targetDate)
  if (!paused) {
    lastUnpausedAnimatedDateRef.current = hudDates.animatedDate
    lastUnpausedTargetDateRef.current = hudDates.targetDate
  }
  // Returns the module-level minimum Date instance itself while clamped, so
  // the memoized graph keeps a stable prop reference at the article top.
  const clampToMinimumGraphDate = (date: Date) =>
    minimumGraphDate && date.getTime() < minimumGraphDate.getTime()
      ? minimumGraphDate
      : date
  const animatedDate = clampToMinimumGraphDate(
    paused ? lastUnpausedAnimatedDateRef.current : hudDates.animatedDate
  )
  const targetDate = clampToMinimumGraphDate(
    paused ? lastUnpausedTargetDateRef.current : hudDates.targetDate
  )
  const lossOfControlHoverReveal = lossOfControlHoverRevealOverride
    ?? (ctx.isHudHovered || ctx.isFirstChoicePlanHovered)
  // Blend both capability-line segments toward the epilogue black (see
  // EPILOGUE_CAPABILITY_LINE_COLOR above). At blend 0 the props pass through
  // untouched: the solid line keeps the variant's color and the dashed line
  // stays on the ⌘+G settings color.
  const backgroundPatternSettings = useBackgroundPatternSettings()
  const epilogueLineBlend = useEpilogueLineColorBlend(ctx.epilogue.isEpilogueHudMode)
  const epilogueCapabilityPastLineColor = epilogueLineBlend > 0
    ? mixHexColors(
      capabilityPastLineColor ?? backgroundPatternSettings.capabilityLineSolidColor,
      EPILOGUE_CAPABILITY_LINE_COLOR,
      epilogueLineBlend,
    )
    : capabilityPastLineColor
  const epilogueCapabilityDashedLineColor = epilogueLineBlend > 0
    ? mixHexColors(
      backgroundPatternSettings.capabilityLineDashedColor,
      EPILOGUE_CAPABILITY_LINE_COLOR,
      epilogueLineBlend,
    )
    : undefined
  // Clicking the chart itself opens the AI Capabilities details. The label
  // flight takes off from the graph's visual box (selectOriginRef), not the
  // hover anchor with its 40px left extension.
  const capabilityOriginRef = useRef<HTMLDivElement | null>(null)
  const capabilityTriggerProps = useMetricTrigger("capability", capabilityOriginRef)
  return (
    <PlaybookExtrapolatedGraph
      triggerProps={detailsTrigger ? capabilityTriggerProps : undefined}
      selectOriginRef={detailsTrigger ? capabilityOriginRef : undefined}
      currentDate={animatedDate}
      targetDate={useAnimatedDateForViewport ? animatedDate : targetDate}
      textColor={ctx.pageTextColor}
      // Track the live epilogue backdrop (--playbook-epilogue-bg, set by
      // Playbook) so marker fills and label strokes match the dimmed page
      // instead of a stale #fffff8. Every consumer is a CSS/SVG color
      // string, so the var() flows through untouched.
      backgroundColor={`var(--playbook-epilogue-bg, ${ctx.pageBackgroundColor})`}
      activePlan={ctx.activePlan}
      hoveredPlan={ctx.hoveredPlan}
      selectedPlan={ctx.selectedPlan}
      choicePointOnScreen={ctx.isChoicePointOnScreen}
      hoverPreviewActive={ctx.isHoverPreviewActive}
      width={width}
      height={height}
      showLossOfControlLines={showLossOfControlLines}
      showAICapabilitiesLabel={showAICapabilitiesLabel}
      redAboveLossOfControl={redAboveLossOfControl}
      redAboveLossOfControlFaded={ctx.epilogue.isEpilogueHudMode}
      // In the galaxies epilogue the trailing-actor-risk (black "too slow")
      // field is dropped entirely — that story has moved past the deal window,
      // so both the black field and its date-driven band injection are off.
      blackBelowTooSlowRisk={ctx.epilogue.isEpilogueHudMode ? false : blackBelowTooSlowRisk}
      trailingActorRiskRecedesWithDate={!ctx.epilogue.isEpilogueHudMode}
      currentYearPill={currentYearPill}
      currentYearPillFlushLeft={ctx.epilogue.isEpilogueHudMode}
      onCurrentYearPillClick={onCurrentYearPillClick}
      currentYearPillMenuOpen={currentYearPillMenuOpen}
      capabilityPastLineColor={epilogueCapabilityPastLineColor}
      capabilityDashedLineColor={epilogueCapabilityDashedLineColor}
      capabilityPastLineStrokeWidth={capabilityPastLineStrokeWidth}
      clipLinesToDitherTop={clipLinesToDitherTop}
      capabilityLineFloorValue={capabilityLineFloorValue}
      showCurrentPointMarker={showCurrentPointMarker}
      currentPointMarkerRadius={currentPointMarkerRadius}
      showCurrentPointCapabilityLabel={showCurrentPointCapabilityLabel}
      showAllYears={showAllYears}
      showYearDots={showYearDots}
      numCapabilityThresholdLabels={numCapabilityThresholdLabels}
      showCapabilityThresholdLabels={showCapabilityThresholdLabels}
      showRiskBackgroundLabels={showRiskBackgroundLabels}
      safetyTaxShiftMonths={safetyTaxShiftMonths}
      alignmentDoublings={alignmentDoublings}
      lossOfControlHoverReveal={lossOfControlHoverReveal}
      animateViewportScale={animateViewportScale}
      animateHeight={animateHeight}
      scaleOverrides={scaleOverrides}
      hideGraphExplainerTooltip={hideGraphExplainerTooltip}
      pointHoverMaxDistanceToLinePx={pointHoverMaxDistanceToLinePx}
      takeoverRiskAreaHover={takeoverRiskAreaHover}
      riskFoomPreview={riskFoomPreview}
      riskFoomDesign={riskFoomDesign}
      riskFoomTedCaption={riskFoomTedCaption}
      riskFoomOnPointHover={riskFoomOnPointHover}
      riskSpaceExplorer={riskSpaceExplorer}
      riskTedGuide={riskTedGuide}
      riskAmbientDisplay={riskAmbientDisplay}
      riskHoverDimDisabled={riskHoverDimDisabled}
      preChoiceYAxisMax={preChoiceYAxisMax}
      useLogScale={graphLogScale}
      highlightLineWhilePointHovered={highlightLineWhilePointHovered}
      riskZoneDimTween={riskZoneDimTween}
      riskZoneDimInverted={riskZoneDimInverted}
      riskZoneDimDotShrink={riskZoneDimDotShrink}
      riskZoneContinuousGuide={riskZoneContinuousGuide}
      pointGuideTedDash={pointGuideTedDash}
    />
  )
}
